import { Phone, Mail, MessageCircle } from "lucide-react";

/* ============================
   CHAT VISUAL — animated WhatsApp-style conversation
   ============================ */
export function ChatVisual() {
  return (
    <div className="cv-stage chat" aria-hidden="true">
      <div className="cv-pill">
        <span className="dot" style={{ background: "#10b981" }} />
        Online
      </div>

      <div className="absolute inset-x-4 top-3 flex items-center gap-2">
        <div className="relative">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#10b981] to-[#0ea271] grid place-items-center text-white font-display font-bold text-sm border-2 border-white shadow">
            A
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#10b981] border-2 border-white" />
        </div>
        <div>
          <div className="text-[11px] font-bold text-[#0a0e1a] leading-tight">ARJ Support</div>
          <div className="text-[9px] text-[#10b981] font-mono-d uppercase tracking-wider">typing…</div>
        </div>
      </div>

      <div className="absolute inset-x-4 bottom-3 flex flex-col gap-1.5">
        <div className="bubble received" style={{ animationDelay: "0.0s" }}>
          Hi! Looking to build a SaaS platform.
        </div>
        <div className="bubble sent self-end" style={{ animationDelay: "0.6s" }}>
          Awesome 🚀 Tell us more!
        </div>
        <div className="bubble received" style={{ animationDelay: "1.4s" }}>
          <span className="typing-dot" style={{ animationDelay: "0s" }} />
          <span className="typing-dot" style={{ animationDelay: "0.18s" }} />
          <span className="typing-dot" style={{ animationDelay: "0.36s" }} />
        </div>
      </div>
    </div>
  );
}

/* ============================
   CALL VISUAL — pulse rings + waveform
   ============================ */
export function CallVisual() {
  return (
    <div className="cv-stage call" aria-hidden="true">
      <div className="cv-pill">
        <span className="dot" style={{ background: "#ff5e57" }} />
        Live · 02:14
      </div>

      {/* Pulse rings */}
      <span className="call-ring" />
      <span className="call-ring" style={{ animationDelay: "0.8s" }} />
      <span className="call-ring" style={{ animationDelay: "1.6s" }} />

      {/* Center phone */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-16 h-16 rounded-full bg-gradient-to-br from-[#0066ff] to-[#0088ff] text-white shadow-[0_14px_30px_-10px_rgba(0,102,255,0.65)] z-[2]">
        <Phone className="w-6 h-6" strokeWidth={2.4} />
      </div>

      {/* Waveform */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 z-[3]">
        {[0.0, 0.12, 0.24, 0.36, 0.48, 0.36, 0.24, 0.12, 0.0].map((d, i) => (
          <span
            key={`wave-${i}`}
            className="wave-bar"
            style={{ animationDelay: `${d}s`, height: `${18 + (i % 3) * 6}px` }}
          />
        ))}
      </div>
    </div>
  );
}

/* ============================
   EMAIL VISUAL — opening envelope + mini mails
   ============================ */
export function EmailVisual() {
  return (
    <div className="cv-stage mail" aria-hidden="true">
      <div className="cv-pill">
        <span className="dot" style={{ background: "#0066ff" }} />
        Inbox · 3 new
      </div>

      {/* Mini mail icons floating */}
      <span className="mini-mail" style={{ left: "16%", top: "30%", animationDelay: "0s" }} />
      <span className="mini-mail" style={{ left: "78%", top: "26%", animationDelay: "1.4s" }} />
      <span className="mini-mail" style={{ left: "82%", top: "60%", animationDelay: "2.6s" }} />
      <span className="mini-mail" style={{ left: "12%", top: "62%", animationDelay: "3.4s" }} />

      {/* Envelope */}
      <div className="envelope" style={{ "--mr": "-2deg" }}>
        <div className="envelope-letter">
          <div className="ll" />
          <div className="ll short" />
          <div className="ll" />
          <div className="ll short" />
          <div className="absolute right-2 bottom-1.5 text-[8px] text-[#0066ff] font-mono-d font-bold">
            ARJ TEAM
          </div>
        </div>
        <div className="envelope-flap" />
      </div>

      {/* Big mail icon at corner */}
      <div className="absolute top-3 left-3 grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-[#1f2937] to-[#0a0e1a] text-white shadow z-[5]">
        <Mail className="w-4 h-4" strokeWidth={2.4} />
      </div>
    </div>
  );
}

/* ============================
   FEATURES BG — floating UI panels behind heading
   ============================ */
export function FeaturesBg() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {/* Code-block panel */}
      <div className="absolute right-[6%] top-[12%] w-[280px] surface-strong rounded-xl p-3 feat-float opacity-90">
        <div className="flex items-center gap-1.5 pb-2 border-b border-[#eef0f5]">
          <i className="w-2 h-2 rounded-full bg-[#ff5e57]" />
          <i className="w-2 h-2 rounded-full bg-[#ffbe3b]" />
          <i className="w-2 h-2 rounded-full bg-[#10b981]" />
          <span className="ml-2 text-[10px] text-[#94a3b8] font-mono-d">deploy.yml</span>
        </div>
        <div className="font-mono-d text-[10px] mt-2 leading-relaxed">
          <div><span className="text-[#7c3aed]">name:</span> <span className="text-[#0066ff]">"production"</span></div>
          <div><span className="text-[#7c3aed]">region:</span> <span className="text-[#0a0e1a]">edge</span></div>
          <div><span className="text-[#7c3aed]">scale:</span> <span className="text-[#10b981]">auto</span></div>
          <div className="text-[#94a3b8]"># 99.9% SLA</div>
        </div>
      </div>

      {/* Network mini diagram */}
      <div className="absolute left-[5%] top-[20%] w-[200px] feat-float-2 opacity-90">
        <svg viewBox="0 0 200 110" className="w-full h-auto">
          <defs>
            <linearGradient id="net-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#0066ff" />
              <stop offset="100%" stopColor="#10b981" />
            </linearGradient>
          </defs>
          <path d="M30 55 C 70 20, 130 20, 170 55" stroke="url(#net-grad)" strokeWidth="2" fill="none" strokeDasharray="4 4">
            <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="1.6s" repeatCount="indefinite" />
          </path>
          <path d="M30 55 C 70 90, 130 90, 170 55" stroke="#10b981" strokeWidth="2" fill="none" strokeDasharray="4 4">
            <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="2s" repeatCount="indefinite" />
          </path>
          <circle cx="30" cy="55" r="10" fill="#fff" stroke="url(#net-grad)" strokeWidth="2" />
          <circle cx="170" cy="55" r="10" fill="#fff" stroke="url(#net-grad)" strokeWidth="2" />
          <circle cx="100" cy="20" r="6" fill="url(#net-grad)" />
          <circle cx="100" cy="90" r="6" fill="#10b981" />
        </svg>
      </div>

      {/* Metric chip */}
      <div className="absolute right-[20%] bottom-[14%] surface-strong rounded-xl px-4 py-2.5 feat-float-3 opacity-95">
        <div className="text-[9px] uppercase tracking-wider text-[#10b981] font-mono-d font-bold">SOC 2</div>
        <div className="font-display text-base font-bold text-[#0a0e1a] -mt-0.5">Compliant</div>
      </div>
    </div>
  );
}

/* ============================
   FORM HEADER VISUAL — animated sparkline + chip
   ============================ */
export function FormHeaderVisual() {
  return (
    <div className="relative w-full h-28 rounded-2xl border border-[#e6ecff] bg-gradient-to-br from-white to-[#f4f7ff] overflow-hidden mb-6" aria-hidden="true">
      <div className="absolute inset-0 bg-dots opacity-50 pointer-events-none" />
      <div className="absolute top-3 left-3 inline-flex items-center gap-2 text-[10px] font-mono-d uppercase tracking-[0.18em] text-[#0066ff] font-bold">
        <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] pulse-dot" />
        Live response · avg 12 min
      </div>

      <svg viewBox="0 0 400 110" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="spark-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0066ff" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(0,102,255,0.18)" />
            <stop offset="100%" stopColor="rgba(0,102,255,0)" />
          </linearGradient>
        </defs>
        <path d="M0,80 C40,60 70,72 100,52 S160,30 200,40 S280,68 320,46 S380,24 400,30 L400,110 L0,110 Z"
              fill="url(#spark-fill)" />
        <path className="spark-line" d="M0,80 C40,60 70,72 100,52 S160,30 200,40 S280,68 320,46 S380,24 400,30" />
      </svg>

      <div className="absolute right-3 bottom-3 inline-flex items-center gap-3 text-xs">
        <div className="flex flex-col items-end">
          <span className="text-[10px] text-[#6b7280] font-mono-d uppercase tracking-wider">leads / week</span>
          <span className="font-display text-lg font-bold text-[#0a0e1a]">+128</span>
        </div>
        <div className="grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-[#0066ff] to-[#10b981] text-white shadow">
          <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
        </div>
      </div>
    </div>
  );
}

/* ============================
   SUPPORT VISUAL — orbiting avatars + chat dots
   ============================ */
export function SupportVisual() {
  return (
    <div className="relative w-full h-44 sm:h-48 rounded-2xl border border-[#e6ecff] bg-gradient-to-br from-white to-[#eff5ff] overflow-hidden" aria-hidden="true">
      <div className="absolute inset-0 bg-dots opacity-50 pointer-events-none" />

      {/* Center hub */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0066ff] to-[#10b981] text-white shadow-[0_14px_30px_-10px_rgba(0,102,255,0.6)] z-[3]">
        <MessageCircle className="w-6 h-6" strokeWidth={2.4} />
      </div>

      {/* Connecting dashed lines */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200" preserveAspectRatio="none">
        <path d="M200 100 L60 50"  stroke="#0066ff" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5">
          <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="1.6s" repeatCount="indefinite" />
        </path>
        <path d="M200 100 L340 50" stroke="#10b981" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5">
          <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="1.8s" repeatCount="indefinite" />
        </path>
        <path d="M200 100 L60 160" stroke="#10b981" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5">
          <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="1.4s" repeatCount="indefinite" />
        </path>
        <path d="M200 100 L340 160" stroke="#0066ff" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5">
          <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="2s" repeatCount="indefinite" />
        </path>
      </svg>

      {/* 4 avatars */}
      <div className="support-avatar online" style={{ left: "10%",  top: "16%", animationDelay: "0s"   }}>R</div>
      <div className="support-avatar online" style={{ right: "10%", top: "16%", animationDelay: "0.6s" }}>J</div>
      <div className="support-avatar online" style={{ left: "10%",  bottom: "12%", animationDelay: "1.2s" }}>S</div>
      <div className="support-avatar online" style={{ right: "10%", bottom: "12%", animationDelay: "1.8s" }}>K</div>

      {/* Floating mini chat bubbles */}
      <div className="absolute left-[26%] top-[28%] bg-white border border-[#e6ecff] rounded-xl rounded-bl-sm px-2.5 py-1 text-[10px] font-mono-d text-[#0a0e1a] shadow-sm">
        Hello!
      </div>
      <div className="absolute right-[26%] bottom-[28%] bg-gradient-to-br from-[#0066ff] to-[#0088ff] text-white rounded-xl rounded-br-sm px-2.5 py-1 text-[10px] font-mono-d shadow-sm">
        On it ✓
      </div>
    </div>
  );
}
